/* eslint-disable max-len, react/forbid-prop-types, object-curly-newline */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronsUpDown, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
} from 'lucide-react';
import EmptyIcon from '../../images/emptyRequests.svg';

// ── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ active, dir }) {
  if (!active) return <ChevronsUpDown size={12} className="text-ink-500" />;
  return dir === 'asc'
    ? <ChevronUp size={12} className="text-ink-900" />
    : <ChevronDown size={12} className="text-ink-900" />;
}
SortIcon.propTypes = { active: PropTypes.bool.isRequired, dir: PropTypes.oneOf(['asc', 'desc']).isRequired };

// ── Embedded pagination (kept here to avoid colliding with legacy components/Pagination) ──
// Pass onPageSizeChange to show a "Rows Per Page" selector (matches Timesheets).
function TablePagination({
  page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  const pages = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const half = Math.floor(max / 2);
    const to = Math.min(totalPages, Math.max(1, page - half) + max - 1);
    const from = Math.max(1, to - max + 1);
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [page, totalPages]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3" style={{ borderTop: '1px solid #f1f2f5' }}>
      <div className="flex items-center gap-1 flex-wrap">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="w-8 h-8 inline-flex items-center justify-center rounded-[6px] text-ink-900 hover:bg-surface-1 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={`min-w-[32px] h-8 px-2 inline-flex items-center justify-center rounded-[6px] text-sm font-medium transition ${p === page ? 'bg-brand-700 text-white' : 'text-ink-900 hover:bg-surface-1'}`}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          aria-label="Next page"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages || total === 0}
          className="w-8 h-8 inline-flex items-center justify-center rounded-[6px] text-ink-900 hover:bg-surface-1 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="flex items-center gap-3 text-sm text-ink-900 flex-wrap">
        {onPageSizeChange ? (
          <>
            <span>Rows Per Page:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 rounded-[6px] text-sm bg-white text-ink-900 focus:outline-none focus:border-brand-500"
              style={{ border: '1px solid #e0e1e6' }}
            >
              {pageSizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <span className="text-ink-500">|</span>
          </>
        ) : null}
        <span className="tabular-nums">{`${start}-${end} of ${total}`}</span>
      </div>
    </div>
  );
}
TablePagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};
TablePagination.defaultProps = {
  onPageSizeChange: null,
  pageSizeOptions: [10, 25, 50, 100],
};

// ── DataTable ──────────────────────────────────────────────────────────────
// columns: [{ key, label, sortable, width, align, render(row) }]
// Pass page/pageSize/total/onPageChange to show the footer pager.
// See DESIGN_SYSTEM.md §5.
// Map a column's `align` to a valid CSS text-align (vertical is always middle).
const hAlign = (a) => (a === 'right' || a === 'center' ? a : 'left');

function DataTable({
  columns, rows, getRowKey, sortKey, sortDir, onSort,
  loading, emptyText, emptyIcon, page, pageSize, total, onPageChange,
  onPageSizeChange, pageSizeOptions, minWidth, onRowClick, fitContent, bare,
}) {
  const hasPager = page != null && pageSize != null && total != null && onPageChange != null;
  // When columns are narrower than the container, table-fixed + w-full stretches
  // every column, leaving big gaps after left-aligned content. fitContent adds a
  // trailing spacer column that absorbs the slack so real columns stay snug.
  const colSpan = columns.length + (fitContent ? 1 : 0);

  const headerCell = (col, i) => {
    const align = hAlign(col.align);
    const padding = i === 0 ? '12px 8px 12px 16px' : '12px 8px';
    const labelEl = (
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 whitespace-nowrap" style={{ letterSpacing: '0.05em', lineHeight: '16px' }}>
        {col.label}
      </span>
    );
    return (
      <th key={col.key} style={{ padding, width: col.flex ? undefined : col.width, textAlign: align, verticalAlign: 'middle' }}>
        {col.sortable && onSort ? (
          <button
            type="button"
            onClick={() => onSort(col.key)}
            className={`inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer ${align === 'right' ? 'flex-row-reverse' : ''}`}
          >
            {labelEl}
            <SortIcon active={sortKey === col.key} dir={sortKey === col.key ? sortDir : 'asc'} />
          </button>
        ) : labelEl}
      </th>
    );
  };

  return (
    <div className={bare ? 'bg-white' : 'rounded-xl bg-white border border-surface-2 shadow-[0_1px_2px_rgba(14,30,64,0.04)] overflow-hidden'}>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed" style={{ minWidth }}>
          <thead>
            <tr className="text-left bg-surface-page">
              {columns.map((col, i) => headerCell(col, i))}
              {fitContent ? <th aria-hidden="true" style={{ width: 'auto' }} /> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="text-center text-sm text-ink-500" style={{ padding: '48px 16px' }}>
                  Loading…
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} style={{ padding: '48px 16px' }}>
                  <div className="flex flex-col items-center gap-3">
                    <img src={emptyIcon || EmptyIcon} alt="" width={48} height={48} />
                    <span className="text-sm text-ink-500">{emptyText}</span>
                  </div>
                </td>
              </tr>
            ) : null}
            {!loading && rows.map((row) => (
              <tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`hover:bg-surface-page transition ${onRowClick ? 'cursor-pointer' : ''}`}
                style={{ borderTop: '1px solid #f1f2f5', fontSize: 14 }}
              >
                {columns.map((col, i) => (
                  <td
                    key={col.key}
                    style={{
                      padding: i === 0 ? '12px 8px 12px 16px' : '12px 8px',
                      textAlign: hAlign(col.align),
                      verticalAlign: 'middle',
                    }}
                    className="text-ink-900"
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
                {fitContent ? <td aria-hidden="true" /> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasPager && !loading && total > 0 ? (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
        />
      ) : null}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node,
    sortable: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flex: PropTypes.bool,
    align: PropTypes.oneOf(['left', 'right', 'center']),
    render: PropTypes.func,
  })).isRequired,
  rows: PropTypes.array.isRequired,
  getRowKey: PropTypes.func.isRequired,
  sortKey: PropTypes.string,
  sortDir: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
  emptyIcon: PropTypes.string,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRowClick: PropTypes.func,
  fitContent: PropTypes.bool,
  bare: PropTypes.bool,
};

DataTable.defaultProps = {
  sortKey: null,
  sortDir: 'asc',
  onSort: null,
  loading: false,
  emptyText: 'No results found.',
  emptyIcon: null,
  page: null,
  pageSize: null,
  total: null,
  onPageChange: null,
  onPageSizeChange: null,
  pageSizeOptions: [10, 25, 50, 100],
  minWidth: undefined,
  onRowClick: null,
  fitContent: false,
  bare: false,
};

export default DataTable;
